import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditsData } from '../../types/story.types';

interface CreditsRendererProps {
  credits: CreditsData;
  onComplete?: () => void;
}

export const CreditsRenderer: React.FC<CreditsRendererProps> = ({ credits, onComplete }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const currentSlide = credits.slides[currentSlideIndex];

  // Initialize Audio
  useEffect(() => {
    if (credits.backgroundMusic) {
      const audio = new Audio(credits.backgroundMusic.url);
      audio.volume = credits.backgroundMusic.volume ?? 1.0;
      audio.loop = credits.backgroundMusic.loop ?? false;
      
      // Playback rate
      if (credits.backgroundMusic.playbackRate) {
        audio.playbackRate = credits.backgroundMusic.playbackRate;
      }

      audio.play().catch(e => console.warn("Audio autoplay blocked:", e));
      audioRef.current = audio;

      return () => {
        audio.pause();
        audioRef.current = null;
      };
    }
  }, [credits.backgroundMusic]);

  // Slide Timer
  useEffect(() => {
    if (!isPlaying || !currentSlide) return;

    const timer = setTimeout(() => {
      if (currentSlideIndex < credits.slides.length - 1) {
        setCurrentSlideIndex(prev => prev + 1);
      } else {
        // Credits finished
        setIsPlaying(false);
        onComplete?.();
      }
    }, currentSlide.duration);

    return () => clearTimeout(timer);
  }, [currentSlideIndex, currentSlide, isPlaying, credits.slides.length, onComplete]);


  if (!currentSlide) return null;

  return (
    <div className="absolute inset-0 bg-black overflow-hidden flex items-center justify-center z-50">
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlideIndex}
          className="relative w-full h-full flex flex-col items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        >
          {/* Visual Content (Image or Video) */}
          <div className={`relative max-w-4xl max-h-[70vh] aspect-[16/9] overflow-hidden bg-black ${currentSlide.effect === 'ken_burns' ? 'overflow-hidden' : ''}`}>
            
            {/* Image */}
            {currentSlide.image && (
              <motion.img
                src={currentSlide.image.url}
                alt="Credit Slide"
                className="w-full h-full object-contain"
                initial={currentSlide.effect === 'ken_burns' ? { scale: 1.0 } : {}}
                animate={currentSlide.effect === 'ken_burns' ? { scale: 1.15 } : {}}
                transition={{ duration: currentSlide.duration / 1000, ease: "linear" }}
              />
            )}

            {/* Video */}
            {currentSlide.video && (
               <VideoSlide video={currentSlide.video} />
            )}
          </div>

          {/* Caption / Text */}
          {currentSlide.text && (
             <motion.div 
                className="mt-8 text-center max-w-2xl px-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1 }}
             >
                <p className="text-white text-xl sm:text-2xl leading-relaxed drop-shadow-md">
                  {currentSlide.text.split('\n').map((line, i) => (
                    <span key={i} className="block mb-2">{line}</span>
                  ))}
                </p>
             </motion.div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Helper component for Video handling to support playbackRate
const VideoSlide: React.FC<{ video: any }> = ({ video }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current && video.playbackRate) {
            videoRef.current.playbackRate = video.playbackRate;
        }
    }, [video.playbackRate]);

    return (
        <video
            ref={videoRef}
            src={video.url}
            className="w-full h-full object-contain"
            autoPlay
            muted={video.muted}
            loop={video.loop}
            playsInline
        />
    );
};
